import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, ChevronRight } from 'lucide-react';
import { siteInfo } from '../data/mock';
import { Button } from './ui/button';

const LOGO_URL =
  "https://scaleupedu.in/logo1.png";

export const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/courses', label: 'Courses' },
    { path: '/about', label: 'About' },
    { path: '/technology', label: 'Technology' },
    { path: '/gallery', label: 'Gallery' },
    { path: '/testimonials', label: 'Testimonials' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-[rgb(17,17,19)]/95 backdrop-blur-xl border-b border-white/10 shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1200px] mx-auto px-6">
        <div className="flex items-center justify-between h-20">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group">
            <img
              src={LOGO_URL}
              alt="Scaleup Academy"
              className="h-26 lg:h-26 w-auto object-contain transition-transform duration-200 group-hover:scale-105"

            />
           
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  isActive(link.path)
                    ? 'text-[#F37021] bg-[rgba(243,112,33,0.1)]'
                    : 'text-[rgb(218,218,218)] hover:text-white hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-3">

            {/* FIXED PHONE (NO WRAP EVER) */}
            <a
              href={`tel:${siteInfo.phone}`}
              className="flex items-center gap-2 text-sm text-[rgb(218,218,218)] hover:text-[#F37021] transition-colors whitespace-nowrap"
            >
              <Phone size={16} className="shrink-0" />
              {siteInfo.phone}
            </a>

            <Link to="/contact">
              <Button className="bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl px-6 h-11 text-sm border-0 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_25px_rgba(243,112,33,0.3)]">
                Enquire Now <ChevronRight size={16} className="ml-1" />
              </Button>
            </Link>
          </div>

          {/* Mobile Toggle */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-white hover:bg-white/10 transition"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[rgb(17,17,19)]/98 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-4 space-y-1">

              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-3 rounded-xl text-base font-medium ${
                    isActive(link.path)
                      ? 'text-[#F37021] bg-[rgba(243,112,33,0.1)]'
                      : 'text-[rgb(218,218,218)] hover:text-white hover:bg-white/5'
                  }`}
                >
                  {link.label}
                </Link>
              ))}

              <div className="pt-4 border-t border-white/10 space-y-3">

                {/* FIXED MOBILE PHONE */}
                <a
                  href={`tel:${siteInfo.phone}`}
                  className="flex items-center gap-4 px-4 py-3 text-[rgb(218,218,218)] hover:text-[#F37021] transition whitespace-nowrap"
                >
                  <Phone size={18} className="shrink-0" />
                  {siteInfo.phone}
                </a>

                <Link to="/contact">
                  <Button className="w-full bg-[#F37021] hover:bg-[#DC5A14] text-white font-semibold rounded-xl h-12">
                    Enquire Now <ChevronRight size={18} className="ml-1" />
                  </Button>
                </Link>

              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
